import { apiClient } from '@/lib/api-client';
import { tenantNow } from '@/lib/tenant-clock';

// Dayparts (Breakfast/Lunch/Dinner/custom) configured in Admin → Menus →
// Dayparts. Used to restrict the POS catalog to items orderable in the
// selected period. Backend: GET /menus/periods (CASHIER+).

export interface PosMenuPeriod {
  id: string;
  location_id: string;
  name: string;
  start_time: string; // "HH:mm" or "HH:mm:ss"
  end_time: string;
  days_of_week: number[];
  product_ids: string[];
  is_active?: boolean;
}

interface MenuPeriodListResponse {
  data: PosMenuPeriod[];
  meta?: unknown;
}

export const menuPeriodService = {
  list(locationId?: string): Promise<MenuPeriodListResponse> {
    const q = new URLSearchParams({ limit: '100' });
    if (locationId) q.set('location_id', locationId);
    return apiClient<MenuPeriodListResponse>(`/menus/periods?${q.toString()}`);
  },
};

/**
 * True when `now` falls within the period's day + time window (handles overnight
 * windows).
 *
 * `now` defaults to the TENANT's wall clock, not the browser's: a daypart of
 * 07:00-11:00 is the restaurant's morning. This used to read the till's own
 * clock, so a Dubai restaurant on a laptop still set to IST switched from
 * breakfast to lunch 1.5 hours early — and disagreed with the server, which
 * evaluates the same window when it prices the order.
 */
export function isPeriodActiveNow(p: PosMenuPeriod, now: Date = tenantNow()): boolean {
  if (!p.days_of_week?.includes(now.getDay())) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  const toMin = (t: string) => {
    const [h, m] = t.slice(0, 5).split(':').map(Number);
    return h * 60 + m;
  };
  const start = toMin(p.start_time);
  const end = toMin(p.end_time);
  return start <= end ? cur >= start && cur <= end : cur >= start || cur <= end;
}
